import React, { useState, useContext, useEffect, useRef } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import RemoveIcon from "@mui/icons-material/Remove";
import CashierApi from "../../utility/CashierApi";
import { CustomerIdContext } from "../../context/CustomerIdProvider";
import debounce from "lodash-es/debounce";
import { setOfflineQuantity, isLocalId } from "../../utility/offline/offlineCart";
import { isOffline } from "../../utility/offline/reachability";

function CartItemQuantity({ item, data }) {
  const { updateData } = CashierApi();
  const { selectedCustomerId } = useContext(CustomerIdContext);
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState(Number(item?.quantity));

  const mutation = useMutation({
    mutationFn: async (data) => {
      const updateLocally = () =>
        setOfflineQuantity(
          selectedCustomerId,
          queryClient.getQueryData(["cart", selectedCustomerId]),
          item.id,
          data.quantity
        );
      if (isLocalId(item.id) || isOffline()) return updateLocally();
      try {
        return await updateData("carts/update/", item.id, data, true);
      } catch (error) {
        if (error?.isNetworkError) return updateLocally();
        throw error;
      }
    },
    retry: false,
    onError: () => {
      setQuantity(Number(item?.quantity));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cart", selectedCustomerId] });
    },
  });

  // Store the debounced function using useRef
  const debounceUpdateRef = useRef(
    debounce((newQuantity) => {
      mutation.mutate({
        product_id: item.id,
        quantity: newQuantity,
        customer_id: selectedCustomerId,
      });
    }, 1000) // 1 second delay
  );

  // Handle Quantity Decrease
  const handleNumSubtract = () => {
    if (quantity > 1) {
      // Prevent quantity from going below 1
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      debounceUpdateRef.current(newQuantity);
    }
  };

  const handleChange = (e) => {
    setQuantity(e.target.value);
    debounceUpdateRef.current(e.target.value);
  };

  // Handle Quantity Increase
  const handleNumAdd = () => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    debounceUpdateRef.current(newQuantity);
  };

  useEffect(() => {
    setQuantity(Number(item?.quantity));
  }, [item]);

  return (
    <div className="flex items-center justify-center gap-1">
      <RemoveIcon
        onClick={handleNumSubtract}
        className={`cursor-pointer ${
          mutation.isLoading ? "text-gray-300" : ""
        }`} // Use isLoading for better UX
      />
      <input
        type="number"
        name="quantity"
        onChange={(e) => handleChange(e)}
        value={quantity}
        className="border-2 w-10 bg-white border-gray-400 text-center text-black rounded-md p-1 focus:border-teal-500 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [appearance:textfield]  "
        style={{
          WebkitAppearance: "none",
          MozAppearance: "textfield",
        }}
      />

      <AddIcon
        onClick={handleNumAdd}
        className={`cursor-pointer ${
          mutation.isLoading ? "text-gray-300" : ""
        }`} // Use isLoading for better UX
      />
    </div>
  );
}

export default CartItemQuantity;
